import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {take} from 'rxjs/operators';
import {combineLatest} from 'rxjs';
import {ZahlungDTO} from '../../openapi/zahlung-openapi';
import {Params} from '@angular/router';
import {AppState} from '../../interfaces/app-state.interface';
import {AppContextSelectors} from '../../store/selectors/app-context.selectors';
import {TransactionSelectors} from '../../store/selectors/transaction.selectors';
import {TransactionActions} from '../../store/actions/transaction.actions';
import {IconName} from '@adnova/jf-ng-components/lib/generic/media/icon/icon.types';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

export interface WizardActionButton {
  title: string;
  href: string;
  icon?: IconName;
  logout?: boolean;
  showInPopup?: boolean;
  queryParams?: Params;
}

@Component({
  selector: 'zlng-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss'],
})
export class WizardTemplateComponent implements OnInit {
  private _store = inject(Store<AppState>);
  private _destroyRef = inject(DestroyRef);

  private _actionButtons: WizardActionButton[] = [];
  private _popupMode?: boolean;

  protected finalPayments: ZahlungDTO[] = [];

  constructor() {}

  ngOnInit(): void {
    this._store
      .select(TransactionSelectors.finalPayments)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(finalPayments => {
        this.finalPayments = finalPayments || [];
      });

    this._store
      .select(AppContextSelectors.wizardActionButtons)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(actionButtons => {
        this._actionButtons = actionButtons || [];
      });

    this._store
      .select(AppContextSelectors.popupMode)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(popupMode => {
        this._popupMode = popupMode;
      });
  }

  /*
   * ActionButtons nur anzeigen, wenn wir NICHT im Popup-Modus sind, oder wenn diese
   * für den Popup-Modus freigegeben wurden. "undefined" wird als default im Popup angezeigt.
   */
  get actionButtons(): WizardActionButton[] {
    return this._actionButtons.filter(
      actionButton => !this._popupMode || actionButton.showInPopup || actionButton.showInPopup === undefined,
    );
  }

  /*
   * Header anzeigen, wenn wegen Sammelüberweisung die Navigation eingeblendet wird
   * oder ActionButtons angezeigt werden sollen.
   */
  get showHeader(): boolean {
    return !!this.finalPayments.length || !!this.actionButtons.length;
  }

  /**
   * Wenn logout = true, werden die Informationen über das bestehende Login des Users aus dem Store entfernt.
   *
   * @param logout Kennzeichnen ob ausgeloggt werden soll
   */
  doLogout(logout?: boolean): void {
    // INFO: Die Session muss immer abgebrochen werden, damit kein invalider Zustand entsteht.
    combineLatest([
      this._store.select(TransactionSelectors.bankingToken),
      this._store.select(TransactionSelectors.clientIp),
      this._store.select(TransactionSelectors.zahlungslauf),
      this._store.select(TransactionSelectors.selectedSecurityfunctionId),
    ])
      .pipe(take(1))
      .subscribe(([token, clientIp, zahlungslauf, selectedSecurityfunctionId]) => {
        if (!zahlungslauf || !token || !clientIp) return;

        this._store.dispatch(
          TransactionActions.cancelPayment({
            inhaberId: zahlungslauf.inhaberId,
            token,
            zahlungslaufId: zahlungslauf.zahlungslaufId,
            clientIp,
            request: {
              auftragsId: zahlungslauf.auftragsId,
              sitzungsSchluessel: zahlungslauf.sitzungsschluessel,
              sicherheitsverfahrenId: selectedSecurityfunctionId,
            },
          }),
        );
      });

    // INFO: Das Ausführungsdatum muss immer zurückgesetzt werden, damit dieses nur für eine Session verwendet wird.
    this._store.dispatch(TransactionActions.resetExecutionDate());

    // INFO: Darüber hinaus prüfen, ob ein Logout erforderlich ist.
    if (logout) {
      this._store.dispatch(TransactionActions.logout());
    }
  }
}
